import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";

interface MobileStoryGridQuadProps {
  stories: ExploreStoryCard[];
  exploreBase: string;
}

/**
 * 2×2 grid of mini cards — The Times / CNBC style.
 * Each cell: image on top + headline below + category.
 * Shows 4 stories in the space of ~1.5 regular cards.
 */
export function MobileStoryGridQuad({
  stories,
  exploreBase,
}: MobileStoryGridQuadProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {stories.map((story) => (
        <GridQuadCell
          exploreBase={exploreBase}
          key={story.storyId}
          story={story}
        />
      ))}
    </div>
  );
}

function GridQuadCell({
  story,
  exploreBase,
}: {
  story: ExploreStoryCard;
  exploreBase: string;
}) {
  const shouldOptimizeImage = canOptimizeExploreImage({
    src: story.coverImageUrl,
  });

  return (
    <Link
      className="group flex flex-col no-underline active:opacity-90"
      href={`${exploreBase}/stories/${story.storyId}`}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)]">
        <Image
          alt=""
          className="h-full w-full object-cover"
          fill
          sizes="(max-width: 640px) 50vw, 25vw"
          src={story.coverImageUrl}
          unoptimized={!shouldOptimizeImage}
        />
        {story.isNew && (
          <div className="absolute top-1.5 left-1.5 flex items-center gap-1 rounded-full bg-[var(--explore-badge-overlay-bg)] px-1.5 py-0.5 shadow-sm backdrop-blur-sm">
            <span className="h-1.5 w-1.5 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)]" />
            <span className="font-bold text-[0.55rem] text-[var(--explore-new-indicator)] uppercase">
              New
            </span>
          </div>
        )}
      </div>

      {/* Text */}
      <div className="flex flex-col pt-2">
        {story.categoryDisplayName && (
          <span className="mb-0.5 font-bold text-[0.6rem] text-[var(--explore-accent-secondary)] uppercase tracking-[0.06em]">
            {story.categoryDisplayName}
          </span>
        )}
        <h3
          className="line-clamp-3 text-pretty font-semibold text-[0.85rem] text-[var(--explore-text-primary)] leading-[1.3]"
          style={{ fontFamily: "var(--explore-card-title-stack)" }}
        >
          {story.headline}
        </h3>
      </div>
    </Link>
  );
}
